/**
 * The router.
 *
 * One tree, one route per destination, five layout shells. Paths are never
 * written here as literals: they come from the shared ROUTES table, so the
 * app and the API agree on what a link means. Pages are lazy so the first
 * paint of the landing does not carry the trading screens with it. The
 * operator console is a separate app (`admin/`) and has no route here.
 */

import { withPageTransitions } from '@metal/ui'
import { lazyRetry } from '@metal/ui/lazy'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { AuthLayout, BareLayout, MemberLayout, PrintLayout, PublicLayout } from './layouts'
import { AccountArea } from './AccountRail'
import { AppRoot } from './AppRoot'
import { RouteGuard, RootRedirect, type GuardKind } from './guards'

/* ---------- Public ------------------------------------------------------- */
const LandingPage = lazyRetry(() => import('@/screens/public/LandingPage'))
const NewsListPage = lazyRetry(() => import('@/screens/public/NewsListPage'))
const NewsArticlePage = lazyRetry(() => import('@/screens/public/NewsArticlePage'))
const AdsPage = lazyRetry(() => import('@/screens/public/AdsPage'))
const LinksPage = lazyRetry(() => import('@/screens/public/LinksPage'))
const NotFoundPage = lazyRetry(() => import('@/screens/public/NotFoundPage'))
const ForbiddenPage = lazyRetry(() => import('@/screens/public/ForbiddenPage'))
const ContentPage = lazyRetry(() => import('@/screens/public/ContentPage'))
const LandingMinimalPage = lazyRetry(() => import('@/screens/public/LandingMinimalPage'))

/* ---------- Market ------------------------------------------------------- */
const MarketPricesPage = lazyRetry(() => import('@/screens/market/MarketPricesPage'))
const MarketLmePage = lazyRetry(() => import('@/screens/market/MarketLmePage'))
const MarketFxPage = lazyRetry(() => import('@/screens/market/MarketFxPage'))
const MarketDashboardPage = lazyRetry(() => import('@/screens/market/MarketDashboardPage'))
const MarketPriceIndexPage = lazyRetry(() => import('@/screens/market/MarketPriceIndexPage'))
const MarketHistoryPage = lazyRetry(() => import('@/screens/market/MarketHistoryPage'))
const MarketArchivePage = lazyRetry(() => import('@/screens/market/MarketArchivePage'))

/* ---------- Entry and onboarding ----------------------------------------- */
const LoginPage = lazyRetry(() => import('@/screens/onboarding/LoginPage'))
const RegisterWizardPage = lazyRetry(() => import('@/screens/onboarding/RegisterWizardPage'))

/* ---------- Member ------------------------------------------------------- */
const DashboardPage = lazyRetry(() => import('@/screens/dashboard/DashboardPage'))
const BoardPage = lazyRetry(() => import('@/screens/board/BoardPage'))
const RfqBoardPage = lazyRetry(() => import('@/screens/board/RfqBoardPage'))
const DealsBoardPage = lazyRetry(() => import('@/screens/board/DealsBoardPage'))

const OfferWizardPage = lazyRetry(() => import('@/screens/offers/OfferWizardPage'))
const BrandCreatePage = lazyRetry(() => import('@/screens/offers/BrandCreatePage'))
const OfferDetailPage = lazyRetry(() => import('@/screens/offers/OfferDetailPage'))
const OfferEditPage = lazyRetry(() => import('@/screens/offers/OfferEditPage'))
const OfferCancelPage = lazyRetry(() => import('@/screens/offers/OfferCancelPage'))
const MyOffersPage = lazyRetry(() => import('@/screens/offers/MyOffersPage'))

const RfqWizardPage = lazyRetry(() => import('@/screens/rfqs/RfqWizardPage'))
const RfqDetailPage = lazyRetry(() => import('@/screens/rfqs/RfqDetailPage'))
const RfqEditPage = lazyRetry(() => import('@/screens/rfqs/RfqEditPage'))
const RfqCancelPage = lazyRetry(() => import('@/screens/rfqs/RfqCancelPage'))
const MyRfqsPage = lazyRetry(() => import('@/screens/rfqs/MyRfqsPage'))

const BidPlacePage = lazyRetry(() => import('@/screens/bids/BidPlacePage'))
const MyBidsPage = lazyRetry(() => import('@/screens/bids/MyBidsPage'))
const BidHistoryPage = lazyRetry(() => import('@/screens/bids/BidHistoryPage'))

const NegotiationPage = lazyRetry(() => import('@/screens/deals/NegotiationPage'))
const DealsListPage = lazyRetry(() => import('@/screens/deals/DealsListPage'))
const DealRoomPage = lazyRetry(() => import('@/screens/deals/DealRoomPage'))
const DealDocumentsPage = lazyRetry(() => import('@/screens/deals/DealDocumentsPage'))
const DealDeliveryPage = lazyRetry(() => import('@/screens/deals/DealDeliveryPage'))
const DealNoticePrintPage = lazyRetry(() => import('@/screens/deals/DealNoticePrintPage'))

const DocumentsLibraryPage = lazyRetry(() => import('@/screens/account/DocumentsLibraryPage'))
const NotificationsPage = lazyRetry(() => import('@/screens/account/NotificationsPage'))
const NotificationSettingsPage = lazyRetry(() => import('@/screens/account/NotificationSettingsPage'))
const WalletPage = lazyRetry(() => import('@/screens/account/WalletPage'))
const WalletTransactionsPage = lazyRetry(() => import('@/screens/account/WalletTransactionsPage'))
const GradeReportPage = lazyRetry(() => import('@/screens/account/GradeReportPage'))
const FavoritesPage = lazyRetry(() => import('@/screens/account/FavoritesPage'))
const RequestsPage = lazyRetry(() => import('@/screens/account/RequestsPage'))
const TicketsPage = lazyRetry(() => import('@/screens/account/TicketsPage'))
const TicketPage = lazyRetry(() => import('@/screens/account/TicketPage'))
const ClaimsPage = lazyRetry(() => import('@/screens/account/ClaimsPage'))
const ClaimCreatePage = lazyRetry(() => import('@/screens/account/ClaimCreatePage'))
const ClaimDetailPage = lazyRetry(() => import('@/screens/account/ClaimDetailPage'))
const SettingsProfilePage = lazyRetry(() => import('@/screens/account/SettingsProfilePage'))
const SettingsUsersPage = lazyRetry(() => import('@/screens/account/SettingsUsersPage'))
const SettingsAddressesPage = lazyRetry(() => import('@/screens/account/SettingsAddressesPage'))
const SettingsKycPage = lazyRetry(() => import('@/screens/account/SettingsKycPage'))
const SettingsSecurityPage = lazyRetry(() => import('@/screens/account/SettingsSecurityPage'))
const MyAdsPage = lazyRetry(() => import('@/screens/ads/MyAdsPage'))
const AdFormPage = lazyRetry(() => import('@/screens/ads/AdFormPage'))

/** Wrap an element in its guard. Every non public route names one. */
function g(kind: GuardKind, element: React.ReactNode, permission?: string) {
  return (
    <RouteGuard kind={kind} permission={permission}>
      {element}
    </RouteGuard>
  )
}

const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/',
        element: (
          <RootRedirect>
            <LandingPage />
          </RootRedirect>
        ),
      },
      { path: '/news', element: <NewsListPage /> },
      { path: '/news/:slug', element: <NewsArticlePage /> },
      { path: '/ads', element: <AdsPage /> },
      { path: '/links', element: <LinksPage /> },
      { path: '/pages/:slug', element: <ContentPage /> },
      { path: '/404', element: <NotFoundPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },

  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterWizardPage /> },
      { path: '/register/:step', element: <RegisterWizardPage /> },
      { path: '/profile2', element: <RegisterWizardPage base="/profile2" wide /> },
      { path: '/profile2/:step', element: <RegisterWizardPage base="/profile2" wide /> },
    ],
  },

  {
    element: <MemberLayout />,
    children: [
      // Open to a file that is not trading yet: the page shows where it stands.
      // The account area: one side panel for every page it opens.
      {
        element: <AccountArea />,
        children: [
          { path: '/dashboard', element: g('authenticated', <DashboardPage />) },
          { path: '/offers/mine', element: g('seller', <MyOffersPage />) },
          { path: '/rfqs/mine', element: g('buyer', <MyRfqsPage />) },
          { path: '/bids', element: g('member', <MyBidsPage />) },
          { path: '/deals', element: g('member', <DealsListPage />) },
          { path: '/documents', element: g('member', <DocumentsLibraryPage />) },
          { path: '/notifications', element: g('authenticated', <NotificationsPage />) },
          { path: '/favorites', element: g('member', <FavoritesPage />) },
          { path: '/requests', element: g('member', <RequestsPage />) },
          { path: '/tickets', element: g('authenticated', <TicketsPage />) },
          { path: '/tickets/:ticketNo', element: g('authenticated', <TicketPage />) },
          // An advert is not a trade: a file not trading yet posts one too.
          { path: '/ads/mine', element: g('authenticated', <MyAdsPage />) },
          { path: '/ads/new', element: g('authenticated', <AdFormPage />) },
          { path: '/ads/:adId/edit', element: g('authenticated', <AdFormPage />) },
          { path: '/wallet', element: g('member', <WalletPage />) },
          { path: '/wallet/transactions', element: g('member', <WalletTransactionsPage />) },
          { path: '/account/grade', element: g('member', <GradeReportPage />) },
          { path: '/claims', element: g('member', <ClaimsPage />) },
          { path: '/settings/profile', element: g('authenticated', <SettingsProfilePage />) },
          { path: '/settings/users', element: g('authenticated', <SettingsUsersPage />) },
          { path: '/settings/addresses', element: g('authenticated', <SettingsAddressesPage />) },
          { path: '/settings/kyc', element: g('authenticated', <SettingsKycPage />) },
          { path: '/settings/notifications', element: g('authenticated', <NotificationSettingsPage />) },
          { path: '/settings/security', element: g('authenticated', <SettingsSecurityPage />) },
        ],
      },
      { path: '/board', element: g('authenticated', <BoardPage />) },
      { path: '/board/rfq', element: g('authenticated', <RfqBoardPage />) },
      { path: '/board/deals', element: g('authenticated', <DealsBoardPage />) },

      // The market screens sit inside the member shell, not the public one:
      // no market number is shown to a visitor who has not signed in.
      { path: '/market', element: <Navigate to="/market/prices" replace /> },
      { path: '/market/prices', element: g('authenticated', <MarketPricesPage />) },
      { path: '/market/lme', element: g('authenticated', <MarketLmePage />) },
      { path: '/market/fx', element: g('authenticated', <MarketFxPage />) },
      { path: '/market/dashboard', element: g('authenticated', <MarketDashboardPage />) },
      { path: '/market/price-index', element: g('authenticated', <MarketPriceIndexPage />) },
      { path: '/market/history', element: g('authenticated', <MarketHistoryPage />) },
      { path: '/market/archive', element: g('authenticated', <MarketArchivePage />) },

      { path: '/offers/:offerId', element: g('authenticated', <OfferDetailPage />) },
      { path: '/offers/:offerId/edit', element: g('seller', <OfferEditPage />) },
      { path: '/offers/:offerId/cancel', element: g('seller', <OfferCancelPage />) },
      { path: '/offers/:offerId/bid', element: g('buyer', <BidPlacePage side="buy" />) },

      { path: '/rfqs/:rfqId', element: g('authenticated', <RfqDetailPage />) },
      { path: '/rfqs/:rfqId/edit', element: g('buyer', <RfqEditPage />) },
      { path: '/rfqs/:rfqId/cancel', element: g('buyer', <RfqCancelPage />) },
      { path: '/rfqs/:rfqId/bid', element: g('seller', <BidPlacePage side="sell" />) },

      { path: '/bids/:bidId/history', element: g('member', <BidHistoryPage />) },

      { path: '/negotiations/:negotiationId', element: g('member', <NegotiationPage />) },
      { path: '/deals/:dealId', element: g('member', <DealRoomPage />) },
      { path: '/deals/:dealId/documents', element: g('member', <DealDocumentsPage />) },
      { path: '/deals/:dealId/delivery', element: g('seller', <DealDeliveryPage />) },

      { path: '/claims/new', element: g('member', <ClaimCreatePage />) },
      { path: '/claims/:claimNo', element: g('member', <ClaimDetailPage />) },

      { path: '/settings', element: <Navigate to="/settings/profile" replace /> },

      { path: '/403', element: g('authenticated', <ForbiddenPage />) },
    ],
  },

  {
    element: <MemberLayout mode="focus" />,
    children: [
      { path: '/offers/new', element: <Navigate to="/offers/new/deal" replace /> },
      { path: '/offers/new/product/brand', element: g('seller', <BrandCreatePage />) },
      { path: '/offers/new/:step', element: g('seller', <OfferWizardPage />) },
      { path: '/rfqs/new', element: <Navigate to="/rfqs/new/deal" replace /> },
      { path: '/rfqs/new/:step', element: g('buyer', <RfqWizardPage />) },
    ],
  },

  {
    element: <BareLayout />,
    children: [{ path: '/landing/minimal', element: <LandingMinimalPage /> }],
  },

  {
    element: <PrintLayout />,
    children: [
      { path: '/deals/:dealId/notice', element: g('member', <DealNoticePrintPage />) },
    ],
  },
]

export const router = withPageTransitions(createBrowserRouter([{ element: <AppRoot />, children: routes }]))
